import { AuditoriaResponseAcao } from '@/api/generated/model';

/**
 * Rótulos e leitura do diff da trilha de auditoria.
 *
 * Ficam aqui, e não nos componentes, porque ação e entidade aparecem em dois
 * lugares (a linha da tabela e o cabeçalho do dialog de detalhe) — escritas
 * diferentes em cada um seriam ruído numa tela que existe para ser conferida.
 */

export const ACOES = [
  AuditoriaResponseAcao.CRIAR,
  AuditoriaResponseAcao.ATUALIZAR,
  AuditoriaResponseAcao.EXCLUIR,
  AuditoriaResponseAcao.ASSINAR,
  AuditoriaResponseAcao.REGISTRAR,
  AuditoriaResponseAcao.CANCELAR,
] as const;

export const ROTULO_ACAO: Record<AuditoriaResponseAcao, string> = {
  CRIAR: 'Criar',
  ATUALIZAR: 'Atualizar',
  EXCLUIR: 'Excluir',
  ASSINAR: 'Assinar',
  REGISTRAR: 'Registrar',
  CANCELAR: 'Cancelar',
};

/**
 * `ASSINAR` e `CANCELAR` ainda não são geradas por nenhum interceptor — estão
 * reservadas para receituário e requisição (B5+). Aparecem no filtro assim
 * mesmo: o enum é o contrato, e uma opção que hoje não traz linha é mais
 * honesta do que a opção sumir quando o módulo entrar.
 */
export const CLASSE_ACAO: Record<AuditoriaResponseAcao, string> = {
  CRIAR: 'text-emerald-600 border-emerald-600/40',
  ATUALIZAR: '',
  EXCLUIR: 'text-destructive border-destructive/40',
  ASSINAR: 'text-brand border-brand/40',
  REGISTRAR: 'text-brand border-brand/40',
  CANCELAR: 'text-destructive border-destructive/40',
};

/**
 * As entidades que hoje geram linha, na ordem em que fazem sentido no filtro.
 *
 * Não há endpoint de "entidades distintas" — esta lista é mantida à mão. Módulo
 * clínico novo (B5+) vai auditar entidade que não está aqui, e é por isso que
 * `rotuloDaEntidade` cai no valor cru em vez de mostrar vazio.
 */
export const ENTIDADES = [
  'usuario',
  'cargo',
  'ambiente',
  'catalogo_exame',
  'analito',
  'referencia_exame',
  'cupom',
  'cupom_categoria',
  'unidade_lancamento',
] as const;

const ROTULO_ENTIDADE: Record<string, string> = {
  usuario: 'Usuário',
  cargo: 'Cargo',
  ambiente: 'Ambiente',
  catalogo_exame: 'Exame do portfólio',
  analito: 'Analito',
  referencia_exame: 'Faixa de referência',
  cupom: 'Cupom',
  cupom_categoria: 'Categoria de cupom',
  unidade_lancamento: 'Lançamento de unidade',
};

export function rotuloDaEntidade(entidade: string): string {
  return ROTULO_ENTIDADE[entidade] ?? entidade;
}

/** O que `calcularDiferenca` grava no lugar do valor de um campo sensível. */
export const VALOR_OCULTO = '[oculto]';

export type Diff = Record<string, [unknown, unknown]>;

/**
 * Estreita `alteracoes` para o formato que o backend realmente grava.
 *
 * O openapi declara o campo como objeto solto, sem `additionalProperties`, e o
 * Orval gera `{ [key: string]: unknown } | null`. Ler `valor[0]` daí sem guarda
 * seria `any` disfarçado — e o que chega é `Record<campo, [antes, depois]>`
 * (`diferenca.util.ts`), então a checagem é barata e o resto da tela fica tipado.
 *
 * Entrada que não bate com o formato é descartada em vez de quebrar a linha: a
 * trilha é histórica, e um registro gravado por uma versão anterior do backend
 * não pode derrubar a página inteira.
 */
export function lerDiff(alteracoes: unknown): Diff | null {
  if (alteracoes === null || typeof alteracoes !== 'object' || Array.isArray(alteracoes)) {
    return null;
  }

  const diff: Diff = {};
  for (const [campo, valor] of Object.entries(alteracoes)) {
    if (Array.isArray(valor) && valor.length === 2) diff[campo] = [valor[0], valor[1]];
  }

  return Object.keys(diff).length > 0 ? diff : null;
}

/**
 * Valor de um lado do diff, legível.
 *
 * Objeto vira JSON indentado, e não `[object Object]`: a exclusão de faixa de
 * referência grava o retrato inteiro do registro (`{ faixa: [{…}, null] }`), que
 * é justamente o caso em que o detalhe importa.
 */
export function formatarValor(valor: unknown): string {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'string') return valor;
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (typeof valor === 'number') return valor.toLocaleString('pt-BR');
  return JSON.stringify(valor, null, 2) ?? '—';
}

/**
 * `<input type="date">` → ISO que o backend aceita.
 *
 * ⚠️ O corte é em **UTC**, não no fuso local. O schema usa `z.iso.datetime()`,
 * que exige o `Z` e rejeita offset (`-03:00` vira 422), e o `criadoEm` é
 * comparado em UTC no service. Em Brasília isso desloca a borda do dia em 3h —
 * filtrar "05/08" pega de 05/08 21:00 a 06/08 20:59 no horário local.
 */
export function inicioDoDiaISO(data: string): string {
  return `${data}T00:00:00.000Z`;
}

export function fimDoDiaISO(data: string): string {
  return `${data}T23:59:59.999Z`;
}
